import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useGlobalCore } from '../../../../../core/modules/global';
import { CREATE_FORMAT_DIALOG } from '../../../../../core/constants/dialog';
import Spinner from '../../../Spinner';
import { useMasterCore } from '../../../../../core/modules/master';
import { useCompanyCore } from '../../../../../core/modules/company';
import { PostFormatPayloadRequest } from '../../../../../core/modules/master/interfaces/post-format';
import { ERROR } from '../../../../../core/constants/errorCode';

const schema = yup.object().shape({
  name: yup.string().required('Campo requerido'),
  description: yup.string().required('Campo requerido'),
});

const CreateDialog = () => {
  const { dialogs, closeDialog } = useGlobalCore();
  const { errorCode, postFormat, getFormats } = useMasterCore();
  const { companyId } = useCompanyCore();

  const [loading, setLoading]  = useState(false);

  const { control, formState, handleSubmit, resetField } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;

  const resetForm = useCallback(() => {
    resetField('name');
    resetField('description');
    closeDialog(CREATE_FORMAT_DIALOG);
  }, [closeDialog, resetField]);

  const onSubmit = useCallback(
    (values: PostFormatPayloadRequest) => {
      if (companyId) {
        setLoading(true);
        postFormat(companyId, values, () => {
          resetForm();
          getFormats(companyId);
          setLoading(false);
        }, () => {
          setLoading(false);
        });
      }
    },
    [companyId, postFormat, resetForm, getFormats]
  );

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && dialogs.includes(CREATE_FORMAT_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Crear Formato
              </Typography>
            </div>
      
            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-24">
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nombre"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.name}
                          helperText={errors?.name?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Descripción"
                          type="textarea"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.description}
                          helperText={errors?.description?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  {errorCode === ERROR.ERR_409 && (
                    <Typography color="error" className="text-md text-center mb-24">
                      Ya existe un formato con ese nombre
                    </Typography>
                  )}

                  <div className="flex justify-center">
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ borderRadius: 50 }}
                      disabled={!isEmpty(errors) || !isValid || loading}
                      type="submit"
                      size="large"
                    >
                      <Spinner show={loading} />
                      Guardar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDialog;
